import type { Instance, ViewState, SearchState } from '@nutrient-sdk/viewer';

// NFKC: compatibility decomposition (expands ligatures like fi) + canonical composition (preserves accented chars like é)
export const expandLigatures = (term: string) => term.normalize('NFKC');

// Strips invisible formatting chars: zero-width spaces, joiners, directional marks,
// word joiners, byte-order marks, and soft hyphens (invisible unless at a line break)
export const stripZeroWidth = (term: string) =>
  term.replace(/[\u00AD\u200B-\u200F\u2060\uFEFF]/gu, '');

export const toFlexibleRegex = (term: string) => {
  const regexSpecialChars = /[.*+?^${}()|[\]\\]/g;
  const escapeWithBackslash = '\\$&';
  // Hyphen/dash variants, whitespace, and quote/apostrophe variants (straight and curly) —
  // AI-extracted quotes and a PDF's text layer don't reliably agree on which variant was
  // used, or whether one is present at all, so all of them fold into one optional gap.
  const flexibleGap = '[-\\s\'"‘’‚‛“”„‟‐‑‒–—]*';
  // Matches three cases, all replaced with flexibleGap (zero or more spaces/hyphens/quotes):
  // 1. [-\s'"‘’‚‛“”„‟‐‑‒–—]+ — one or more of those chars between words (e.g. "Carlos- III")
  // 2. (?=\\[.,;:!?])        — zero-width position before regex-escaped punctuation (e.g. before \. or \?)
  // 3. (?=[,;!:])            — zero-width position before unescaped punctuation (e.g. before , or !)
  // Cases 2 & 3 allow optional whitespace before punctuation (e.g. "ications ." in scanned PDFs)
  const whitespaceOrHyphen = /[-\s'"‘’‚‛“”„‟‐‑‒–—]+|(?=\\[.,;:!?])|(?=[,;!:])/g;

  const escaped = term.replace(regexSpecialChars, escapeWithBackslash);
  return escaped.replace(whitespaceOrHyphen, flexibleGap);
};

const tryPlainSearch = async (
  instance: Instance,
  term: string
): Promise<number> => {
  const results = await instance.search(term);
  if (results?.size > 0) {
    instance.startUISearch(term);
    return results.size;
  }
  return 0;
};

const tryRegexSearch = async (
  instance: Instance,
  pattern: string
): Promise<number> => {
  const results = await instance.search(pattern, { searchType: 'regex' });
  const pageIndex = results?.get?.(0)?.pageIndex ?? 0;

  if (!results?.size) {
    return 0;
  }

  instance.setViewState((viewState: ViewState) =>
    viewState.set('currentPageIndex', pageIndex)
  );
  instance.setSearchState((searchState: SearchState) =>
    searchState.set('term', pattern).set('results', results)
  );

  return results.size;
};

export const searchWithFallback = async (
  instance: Instance,
  term: string
): Promise<number> => {
  // 1. Try searching for the term without any modification
  const results = await tryPlainSearch(instance, term);
  if (results > 0) return results;

  // 2. Try searching with the ligatures expanded
  const withLigatures = expandLigatures(term);
  const ligaturesResults = await tryPlainSearch(instance, withLigatures);
  if (ligaturesResults > 0) return ligaturesResults;

  // 3. Try searching with the zero width characters removed
  const cleaned = stripZeroWidth(withLigatures);
  const cleanedResults = await tryPlainSearch(instance, cleaned);
  if (cleanedResults > 0) return cleanedResults;

  // 4. Regex fallback: flexible whitespace/hyphen matching (e.g. "Carlos- III" → "Carlos-III")
  for (const pattern of [toFlexibleRegex(term), toFlexibleRegex(cleaned)]) {
    const regexResults = await tryRegexSearch(instance, pattern);
    if (regexResults > 0) return regexResults;
  }

  // 5. Prefix regex: long quotes can exceed Nutrient's regex length limit (~500 chars)
  const prefixResults = await tryRegexSearch(
    instance,
    toFlexibleRegex(cleaned.slice(0, 400))
  );
  if (prefixResults > 0) return prefixResults;

  return 0;
};
